import type { Folder } from "@esi/shared";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { t as translate } from "../../i18n/i18n.ts";
import { useFolders } from "../folders.ts";
import { PAGE_TYPES } from "./behaviour.ts";

/** `wiki.placement`: a Wiki folder path (names from the root) per page type. */
export type Placement = Partial<Record<(typeof PAGE_TYPES)[number], string[]>>;

const asPlacement = (value: unknown): Placement =>
  value && typeof value === "object" ? (value as Placement) : {};

/** "Summaries: Sources › Summaries · Concepts: Glossary", or that every type starts at the root. */
export function describePlacement(value: unknown): string {
  const placement = asPlacement(value);
  const set = PAGE_TYPES.flatMap((type) => {
    const path = placement[type];
    return path?.length
      ? [
          translate("settings.behaviour.placement.entry", {
            type: translate(`wiki.pageTypes.${type}`),
            path: path.join(" › "),
          }),
        ]
      : [];
  });
  return set.length ? set.join(" · ") : translate("settings.behaviour.placement.none");
}

/** Every Wiki folder of the project with its path from the root, in path order. */
function wikiFolderPaths(folders: readonly Folder[]): { id: string; path: string[] }[] {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const pathOf = (folder: Folder): string[] => {
    const path: string[] = [];
    for (let f: Folder | undefined = folder; f && path.length <= folders.length; ) {
      path.unshift(f.name);
      f = f.parentId ? byId.get(f.parentId) : undefined;
    }
    return path;
  };
  return folders
    .filter((f) => f.section === "wiki")
    .map((f) => ({ id: f.id, path: pathOf(f) }))
    .sort((a, b) => a.path.join("\u0000").localeCompare(b.path.join("\u0000")));
}

const samePath = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((name, i) => name.toLowerCase() === b[i]?.toLowerCase());

/**
 * The project's editor (Knowledge page): one folder per page type, picked among the project's
 * Wiki folders, or the Wiki root. A path set above the project that this project lacks stays
 * listed as such, so saving never drops it silently.
 */
export function PlacementEditor(props: {
  id: string;
  projectId: string;
  value: unknown;
  onChange: (value: Placement) => void;
}) {
  const { id, projectId, onChange } = props;
  const { t } = useTranslation();
  const value = asPlacement(props.value);
  const folders = useFolders(projectId).data?.folders;
  const options = useMemo(() => wikiFolderPaths(folders ?? []), [folders]);
  return (
    <div className="flex flex-col gap-3">
      {PAGE_TYPES.map((type) => {
        const current = value[type] ?? [];
        const known = options.find((o) => samePath(o.path, current));
        const selected = current.length === 0 ? "" : JSON.stringify(known?.path ?? current);
        return (
          <div
            key={type}
            className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
          >
            <Label htmlFor={`${id}-${type}`} className="font-normal">
              {t(`wiki.pageTypes.${type}`)}
            </Label>
            <NativeSelect
              id={`${id}-${type}`}
              value={selected}
              className="sm:w-72"
              onChange={(e) => {
                const raw = e.currentTarget.value;
                const next = { ...value };
                if (raw) {
                  next[type] = JSON.parse(raw) as string[];
                } else {
                  delete next[type];
                }
                onChange(next);
              }}
            >
              <option value="">{t("settings.behaviour.placement.root")}</option>
              {current.length && !known ? (
                <option value={selected}>
                  {t("settings.behaviour.placement.missing", { path: current.join(" › ") })}
                </option>
              ) : null}
              {options.map((o) => (
                <option key={o.id} value={JSON.stringify(o.path)}>
                  {o.path.join(" › ")}
                </option>
              ))}
            </NativeSelect>
          </div>
        );
      })}
      <p className="text-xs text-fg-muted">{t("settings.behaviour.placement.hint")}</p>
    </div>
  );
}

/** A path typed in the backoffice: names separated by "/" (a folder name holds no "/"). */
const parsePath = (text: string) =>
  text
    .split("/")
    .map((name) => name.trim())
    .filter((name) => name.length > 0);

/**
 * The backoffice's editor (Knowledge group): a path typed per page type, as an entity's setting
 * applies to every project that has a folder at that path.
 */
export function PlacementPathsEditor(props: {
  id: string;
  value: unknown;
  onChange: (value: Placement) => void;
}) {
  const { id, onChange } = props;
  const { t } = useTranslation();
  const value = asPlacement(props.value);
  return (
    <div className="flex flex-col gap-3">
      {PAGE_TYPES.map((type) => (
        <div
          key={type}
          className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
        >
          <Label htmlFor={`${id}-${type}`} className="font-normal">
            {t(`wiki.pageTypes.${type}`)}
          </Label>
          <Input
            id={`${id}-${type}`}
            defaultValue={(value[type] ?? []).join(" / ")}
            placeholder={t("settings.behaviour.placement.pathPlaceholder")}
            aria-describedby={`${id}-paths-hint`}
            className="sm:w-72"
            onChange={(e) => {
              const path = parsePath(e.currentTarget.value);
              const next = { ...value };
              if (path.length) {
                next[type] = path;
              } else {
                delete next[type];
              }
              onChange(next);
            }}
          />
        </div>
      ))}
      <p id={`${id}-paths-hint`} className="text-xs text-fg-muted">
        {t("settings.behaviour.placement.pathsHint")}
      </p>
    </div>
  );
}
